import {
  ImageScaleView,
  ImageView,
  ImageViewOpts,
} from '@play-co/timestep-core/ui';
import StateObserver, { now, user } from 'src/StateObserver';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { assertNever } from 'src/replicant/utils';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import buttons from 'src/lib/ui/config/buttons';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { cardsCollectRewards, cardsOpenInboxPopup } from '../cards.sequence';
import { downscale } from 'src/conf/scale';
import { CardsProgressBar } from './CardsProgressBar';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import {
  cardsHasAnyPendingTrades,
  cardsHasPendingTrades,
} from 'src/replicant/cards/cards.getters';
import { CardsShine } from './CardsShine';

type CommonState = {
  collected: number;
  totalCards: number;
};
type InProgress = {
  stateName: 'inProgress';
  rewards: Match2Reward[];
} & CommonState;
type RewardsAvailable = {
  stateName: 'rewardsAvailable';
  rewards: Match2Reward[];
} & CommonState;
type Complete = { stateName: 'complete' } & CommonState;
export type CollectionsHeaderState = InProgress | RewardsAvailable | Complete;
type CardCollectionsHeaderOpts = ImageViewOpts & {
  onInboxClose?: () => void | Promise<void>;
};

const REWARD_ICON = 'assets/ui/cards/common/icon_chest.png';
const REWARDS_Y = downscale(380);

export class CardCollectionsHeader extends ImageView {
  private totalCards: number = 0;
  private progress: CardsProgressBar;
  private setCardProgress: (collected: number, totalCards: number) => void;
  private claimButton: ButtonScaleViewWithText;
  private inboxButton: ButtonScaleView;
  private inboxNotification: ImageScaleView;
  private rewardsImage: ImageScaleView;
  private rewardsShine: CardsShine;
  private onInboxClose?: () => void | Promise<void>;

  constructor(opts: CardCollectionsHeaderOpts) {
    const { onInboxClose, ...viewOpts } = opts;
    super({
      image: 'assets/ui/cards/popupCardCollections/header.png',
      width: downscale(1080),
      height: downscale(690),
      ...viewOpts,
    });
    this.onInboxClose = onInboxClose;

    new ImageScaleView({
      superview: this,
      image: `assets/ui/cards/common/title_${StateObserver.getLocale()}.png`,
      width: downscale(682),
      height: downscale(121),
      x: this.style.width / 2 + 5,
      y: 45,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.rewardsShine = new CardsShine({
      superview: this,
      x: this.style.width * 0.5,
      y: REWARDS_Y,
      width: 220,
      height: 220,
      radius: 110,
      opacity: 0.55,
      visible: false,
    });

    this.rewardsImage = new ImageScaleView({
      superview: this,
      image: 'assets/ui/cards/popupCardCollections/rewards.png',
      width: downscale(569),
      height: downscale(190),
      x: this.style.width * 0.5,
      y: REWARDS_Y,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      visible: false,
      canHandleEvents: false,
    });

    this.progress = new CardsProgressBar({
      superview: this,
      x: this.style.width * 0.5,
      y: this.style.height - 60,
      width: this.style.width - 140,
      centerOnOrigin: true,
      leftIcon: 'assets/ui/cards/common/icon_cards.png',
      rightIcon: REWARD_ICON,
    });

    this.claimButton = new ButtonScaleViewWithText({
      superview: this,
      ...buttons.secondary,
      width: 300,
      height: 90,
      fontSize: 46,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: this.style.height - 90,
      font: 'Body',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.COLLECT_ALBUM_BUTTON,
      },
      localeText: () => i18n('cards.claim'),
      onClick: () => this.collectAlbumRewards(),
    });

    this.inboxButton = new ButtonScaleView({
      superview: this,
      image: 'assets/ui/cards/popupCardCollections/inbox.png',
      width: downscale(170),
      height: downscale(174),
      centerOnOrigin: true,
      x: 70,
      y: 255,
      visible: false,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.INBOX_BUTTON,
      },
      onClick: () => this.openInbox(),
    });

    this.inboxNotification = new ImageScaleView({
      superview: this.inboxButton,
      image: 'assets/ui/shared/icons/notification_badge.png',
      width: 40,
      height: 40,
      x: 100,
      y: 10,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      visible: false,
      canHandleEvents: false,
    });

    this.setCardProgress = (collected: number, totalCards: number) => {
      this.totalCards = totalCards;
      this.progress.setProgress(collected, totalCards);
    };
  }

  public setState(state: CollectionsHeaderState) {
    this.setCardProgress(state.collected, state.totalCards);
    this.updateInboxButton();

    switch (state.stateName) {
      case 'inProgress':
        this.setupInProgress(state.rewards);
        break;
      case 'complete':
        this.setupComplete();
        break;
      case 'rewardsAvailable':
        this.setupRewardsAvailable(state.rewards);
        break;
      default:
        assertNever(state);
    }
  }

  private updateInboxButton() {
    const hasInbox = cardsHasAnyPendingTrades(user(), now());
    const hasNotification = cardsHasPendingTrades(user(), now());
    this.inboxButton.updateOpts({ visible: hasInbox });
    this.inboxNotification.updateOpts({ visible: hasNotification });
  }

  private setupInProgress(rewards: Match2Reward[]) {
    this.claimButton.hide();
    this.progress.show();
    this.progress.setTitle(i18n('cards.grandPrize'));
    this.progress.setRightIcon(REWARD_ICON);
    this.progress.setRewards(rewards);
    this.showAlbumRewards();
  }

  private setupRewardsAvailable(rewards: Match2Reward[]) {
    this.claimButton.show();
    this.progress.hide();
    this.progress.setRewards(rewards);
    this.showAlbumRewards();
  }

  private setupComplete() {
    this.claimButton.hide();
    this.progress.show();
    this.progress.setTitle(i18n(`cards.albumComplete`));
    this.progress.setRightIcon(undefined);
    this.progress.setRewards(undefined);
    this.hideAlbumRewards();
  }

  private showAlbumRewards() {
    this.rewardsShine.show();
    this.rewardsShine.autoUpdate();
    this.rewardsImage.show();
  }

  private hideAlbumRewards() {
    this.rewardsShine.hide();
    this.rewardsShine.stopAutoUpdate();
    this.rewardsImage.hide();
  }

  private async collectAlbumRewards() {
    sounds.sfx.play('ok');

    // Collect the rewards and wait for the result
    const success = await cardsCollectRewards('album');

    // Bail if not successful
    if (!success) return;

    // If successful, reset the header to the complete state
    // We assume that the album is complete after collecting the rewards, so we can display it with all cards
    this.setState({
      stateName: 'complete',
      collected: this.totalCards,
      totalCards: this.totalCards,
    });
  }

  private async openInbox() {
    sounds.sfx.play('ok');
    const opened = await cardsOpenInboxPopup();
    if (opened) await this.onInboxClose?.();
    this.updateInboxButton();
  }
}
